import { Button } from "../../../components/buttons/button";
import { SearchInput } from "../../../components/inputs/searchInput";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { SettingsLayout } from "../../../components/layout/settingsLayout";
import { AddHolidayFlow } from "../../../components/modal/app_modals/addHolidayFlow";
import Dropdown from "../../../components/dropdown/dropdown";
import { useState } from "react";
import { currentYear, nextYear } from "../../../utils/constants";
import { TableComponent } from "../../../components/table";
import { Pill } from "../../../components/pill";
import { QueryWithStatus } from "../../../utils/types";
import { useGetAllHolidaysQuery } from "../../../redux/features/admin/admin.api";
import { HolidayDateBadge } from "../../../components/dateBadge";
import { formatDate } from "../../../utils/helpers";
import { ConfirmPublishedHoliday } from "../../../components/modal/app_modals/confirmPublishedHoliday";

export const HolidaysPage = () => {
  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();
  const [query, setQuery] = useState<
    QueryWithStatus & { year: number | undefined }
  >({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
    status: undefined,
    year: undefined,
  });

  const [year, setYear] = useState<number>(currentYear);
  const [selectHoliday, setSelectHoliday] = useState<{
    _id: string;
    name: string;
    holidayDate: string;
    nextWorkingDay: string;
  } | null>(null);

  const dropDownOptions = [
    {
      label: currentYear,
      value: currentYear,
      onClick: () => {
        setYear(currentYear);
      },
    },
    {
      label: nextYear,
      value: nextYear,
      onClick: () => {
        setYear(nextYear);
      },
    },
  ];
  const {
    isFetching: fetchingHolidays,
    data: holidayData,
    refetch: refetchHolidays,
  } = useGetAllHolidaysQuery(
    { ...query, year, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  return (
    <div className="w-full">
      <PageHeader header={"Settings"} className="font-fraunces font-normal" />
      <main className="mt-10 flex flex-col items-start gap-[30px] md:flex-row md:gap-[80px]">
        <SettingsLayout />
        <div className="flex w-full flex-col gap-[2px]">
          <div className="flex h-[58px] cursor-pointer items-center justify-between rounded-2xl bg-white p-3">
            <h1 className="font-fustat text-base font-bold text-grey-950">
              Public Holidays
            </h1>
            <Button
              text="Add Holiday"
              icon="plus"
              type="button"
              onClick={() => {
                handleModalOpen("addHolidayModal");
              }}
              variation="blue"
              iconClassName={"mb-1"}
              className="max-w-fit"
            />
          </div>
          <BgWrapper className="flex flex-col gap-3 rounded-t-2xl">
            <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
              <SearchInput
                searchFunction={(value) => {
                  setQuery({
                    ...query,
                    search: value,
                    page: 1,
                  });
                }}
                placeholder="Search"
                className="md:!max-w-[300px]"
              />
              <Dropdown
                menuItems={dropDownOptions.map((option) => {
                  return {
                    name: (
                      <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                        {option.label}
                      </p>
                    ),
                    onClick: option.onClick,
                  };
                })}
                className=""
                menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px]"
                menuItemsClassName="border-none !text-base !bg-transparent !p-0"
                position="bottom end"
              >
                <Button
                  text={year}
                  icon="caret-down"
                  type="button"
                  iconFirst={false}
                  variation="styled-grey"
                  iconClassName={"mb-1"}
                  className="max-w-fit"
                />
              </Dropdown>
            </div>
            <TableComponent
              headers={[]}
              rows={
                holidayData?.data?.holidays?.map((holiday, idx) => {
                  return {
                    id: idx,
                    items: [
                      <div
                        onClick={() => {
                          if (holiday?.status === "requires_attention") {
                            setSelectHoliday({
                              _id: holiday._id,
                              name: holiday.name,
                              nextWorkingDay: holiday.nextWorkingDay,
                              holidayDate: holiday.holidayDate,
                            });
                            handleModalOpen("confirmPublishedHolidayModal");
                          }
                        }}
                        className={`flex w-full flex-1 flex-col justify-between gap-2 md:flex-row md:items-center`}
                      >
                        <div className="flex items-center gap-[2px]">
                          <div className="mr-2">
                            <HolidayDateBadge
                              holidayDate={holiday?.holidayDate}
                            />
                          </div>

                          <p className="text-sm font-bold text-grey-900">
                            {holiday.name} ∙
                          </p>
                          <p className="text-sm text-grey-600">
                            work resumes {formatDate(holiday.nextWorkingDay)}
                          </p>
                        </div>
                        <div className="cursor-pointer">
                          <Pill
                            text={`${holiday?.status === "requires_attention" ? "REQUIRES ATTENTION" : holiday?.status === "completed" ? "COMPLETED" : "CONFIRMED"}`}
                            variation={`${holiday?.status === "requires_attention" ? "yellow" : holiday?.status === "completed" ? "green" : "blue"}`}
                          />
                        </div>
                      </div>,
                    ],
                  };
                }) ?? []
              }
              loadingRows={query.limit}
              isLoading={fetchingHolidays}
              isRowClickable={false}
              isTableEmpty={[0, undefined].includes(
                holidayData?.data?.holidays?.length,
              )}
              emptyTitle="No Holidays"
              emptySubTitle=""
              emptyImage="/assets/images/guest-glass.png"
              isPaginated
              currentPage={query.page ?? 1}
              totalCount={holidayData?.data?.totalCount ?? 0}
              itemsPerPage={query.limit!}
              pageRangeDisplayed={3}
              onPageChange={(page) => {
                setQuery({
                  ...query,
                  page: page,
                });
              }}
              className=""
            />
          </BgWrapper>
        </div>
      </main>
      {modalOpen && (
        <AddHolidayFlow
          open={modal === "addHolidayModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          refetchHolidays={refetchHolidays}
          onSuccess={() => {
            console.log("Holiday added successfully");
          }}
        />
      )}
      {modalOpen && (
        <ConfirmPublishedHoliday
          open={modal === "confirmPublishedHolidayModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          holidayId={selectHoliday?._id ?? ""}
          holidayName={selectHoliday?.name ?? ""}
          holidayDate={selectHoliday?.holidayDate ?? ""}
          nextWorkingDay={selectHoliday?.nextWorkingDay ?? ""}
          refetchHolidays={refetchHolidays}
          onSuccess={() => {
            console.log("Holiday added successfully");
          }}
        />
      )}
    </div>
  );
};
